import { supabase } from '../lib/supabase';

// ─── Phone normalization ───────────────────────────────────────────────────────
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone?.trim()?.replace(/[\s\-\.\(\)]/g, '');
  if (cleaned?.startsWith('0033')) cleaned = '+33' + cleaned?.slice(4);
  if (cleaned?.startsWith('+33')) cleaned = '0' + cleaned?.slice(3);
  if (cleaned?.length === 9 && /^[1-9]/?.test(cleaned)) cleaned = '0' + cleaned;
  return cleaned;
};

// ─── Status labels ────────────────────────────────────────────────────────────
export const PROMO_STATUS_LABELS = {
  commande_creee: 'Commande créée',
  barriques_a_livrer: 'Barriques à livrer',
  barriques_livrees: 'Barriques livrées',
  acompte_paye: 'Acompte payé',
  en_attente_remplissage: 'En attente de remplissage client',
  relance_necessaire: 'Relance client nécessaire',
  collecte_planifiee: 'Collecte planifiée',
  barriques_collectees: 'Barriques collectées',
  solde_paye: 'Solde payé',
  converti_en_envoi: 'Converti en envoi',
  affecte_conteneur: 'Affecté à un conteneur',
};

export const PROMO_STATUS_COLORS = {
  commande_creee: 'bg-gray-100 text-gray-700',
  barriques_a_livrer: 'bg-blue-100 text-blue-700',
  barriques_livrees: 'bg-indigo-100 text-indigo-700',
  acompte_paye: 'bg-teal-100 text-teal-700',
  en_attente_remplissage: 'bg-yellow-100 text-yellow-700',
  relance_necessaire: 'bg-red-100 text-red-700',
  collecte_planifiee: 'bg-purple-100 text-purple-700',
  barriques_collectees: 'bg-orange-100 text-orange-700',
  solde_paye: 'bg-green-100 text-green-700',
  converti_en_envoi: 'bg-emerald-100 text-emerald-700',
  affecte_conteneur: 'bg-cyan-100 text-cyan-700',
};

export const PROMO_STATUS_ORDER = [
  'commande_creee',
  'barriques_a_livrer',
  'barriques_livrees',
  'acompte_paye',
  'en_attente_remplissage',
  'relance_necessaire',
  'collecte_planifiee',
  'barriques_collectees',
  'solde_paye',
  'converti_en_envoi',
  'affecte_conteneur',
];

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export const promoSummerOrderService = {
  // List all orders with optional filters
  async getAll({ status, clientId, search } = {}) {
    let query = supabase?.from('promo_summer_orders')?.select('*')?.order('created_at', { ascending: false });

    if (status && status !== 'all') {
      query = query?.eq('order_status', status);
    }
    if (clientId) {
      query = query?.eq('client_id', clientId);
    }
    if (search) {
      query = query?.or(
        `client_name.ilike.%${search}%,client_phone.ilike.%${search}%,delivery_address.ilike.%${search}%`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Get single order
  async getById(id) {
    const { data, error } = await supabase?.from('promo_summer_orders')?.select('*')?.eq('id', id)?.single();
    if (error) throw error;
    return data;
  },

  // Get orders for a specific client
  async getByClientId(clientId) {
    const { data, error } = await supabase?.from('promo_summer_orders')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // ─── Find or create client for promo order ───────────────────────────────
  async findOrCreatePromoClient(orderData) {
    const rawPhone = orderData?.clientPhone?.trim() || null;
    const normalizedPhone = normalizePhone(rawPhone);
    const email = orderData?.clientEmail?.trim() || null;
    const name = orderData?.clientName?.trim();

    // 1. Search by normalized phone
    if (normalizedPhone) {
      const { data: byPhone } = await supabase
        ?.from('clients')
        ?.select('id')
        ?.eq('normalized_phone', normalizedPhone)
        ?.limit(1)
        ?.single();
      if (byPhone?.id) {
        const { data: existing } = await supabase?.from('clients')?.select('promo_orders_count')?.eq('id', byPhone?.id)?.single();
        await supabase?.from('clients')?.update({
          promo_orders_count: (existing?.promo_orders_count || 0) + 1,
        })?.eq('id', byPhone?.id);
        return byPhone?.id;
      }
    }

    // 2. Search by email
    if (email) {
      const { data: byEmail } = await supabase
        ?.from('clients')
        ?.select('id')
        ?.eq('email', email)
        ?.limit(1)
        ?.single();
      if (byEmail?.id) {
        const { data: existing } = await supabase?.from('clients')?.select('promo_orders_count')?.eq('id', byEmail?.id)?.single();
        await supabase?.from('clients')?.update({
          promo_orders_count: (existing?.promo_orders_count || 0) + 1,
        })?.eq('id', byEmail?.id);
        return byEmail?.id;
      }
    }

    // 3. Create new promo client
    const newClient = {
      name: name || 'Client inconnu',
      phone: rawPhone || null,
      normalized_phone: normalizedPhone || null,
      email: email || null,
      address: orderData?.deliveryAddress || null,
      city: orderData?.deliveryCity || null,
      postal_code: orderData?.deliveryPostalCode || null,
      client_status: 'promo_customer',
      source: 'Promo Été 2026',
      promo_orders_count: 1,
      nb_shipments: 0,
      loyalty_points_total: 0,
      account_status: 'active',
    };

    const { data: created, error: createError } = await supabase
      ?.from('clients')
      ?.insert(newClient)
      ?.select('id')
      ?.single();

    if (createError) {
      console.error('Erreur création client promo:', createError);
      return null;
    }
    return created?.id;
  },

  // Create new order
  async create(orderData) {
    // ─── Auto-create or link client ───────────────────────────────────────
    let resolvedClientId = orderData?.clientId || null;
    if (!resolvedClientId) {
      try {
        resolvedClientId = await this.findOrCreatePromoClient(orderData);
      } catch (err) {
        console.error('Erreur lors de la liaison client promo:', err);
        // Continue without client_id rather than blocking the order
      }
    }

    const payload = {
      client_id: resolvedClientId,
      client_name: orderData?.clientName,
      client_phone: orderData?.clientPhone || null,
      client_email: orderData?.clientEmail || null,
      barrel_quantity: orderData?.barrelQuantity || 1,
      unit_promo_price: 160.00,
      unit_deposit_amount: 50.00,
      unit_balance_amount: 110.00,
      unit_provider_cost: orderData?.selectedPurchasePrice != null ? Number(orderData?.selectedPurchasePrice) : 30.00,
      unit_adt_revenue: orderData?.selectedAdtMargin != null ? Number(orderData?.selectedAdtMargin) : 20.00,
      provider_name: orderData?.providerName || null,
      provider_phone: orderData?.providerPhone || null,
      provider_id: orderData?.providerId || null,
      provider_product_id: orderData?.providerProductId || null,
      selected_product_name: orderData?.selectedProductName || null,
      selected_purchase_price: orderData?.selectedPurchasePrice != null ? Number(orderData?.selectedPurchasePrice) : null,
      selected_client_price: orderData?.selectedClientPrice != null ? Number(orderData?.selectedClientPrice) : null,
      selected_adt_margin: orderData?.selectedAdtMargin != null ? Number(orderData?.selectedAdtMargin) : null,
      delivery_date: orderData?.deliveryDate || null,
      delivery_address: orderData?.deliveryAddress || null,
      delivery_city: orderData?.deliveryCity || null,
      delivery_postal_code: orderData?.deliveryPostalCode || null,
      order_status: 'commande_creee',
      notes: orderData?.notes || null,
    };

    const { data, error } = await supabase?.from('promo_summer_orders')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Update order
  async update(id, updates) {
    const payload = {};
    if (updates?.clientName !== undefined) payload.client_name = updates?.clientName;
    if (updates?.clientPhone !== undefined) payload.client_phone = updates?.clientPhone;
    if (updates?.clientEmail !== undefined) payload.client_email = updates?.clientEmail;
    if (updates?.barrelQuantity !== undefined) payload.barrel_quantity = updates?.barrelQuantity;
    if (updates?.providerName !== undefined) payload.provider_name = updates?.providerName;
    if (updates?.providerPhone !== undefined) payload.provider_phone = updates?.providerPhone;
    if (updates?.deliveryDate !== undefined) payload.delivery_date = updates?.deliveryDate;
    if (updates?.deliveryAddress !== undefined) payload.delivery_address = updates?.deliveryAddress;
    if (updates?.deliveryCity !== undefined) payload.delivery_city = updates?.deliveryCity;
    if (updates?.deliveryPostalCode !== undefined) payload.delivery_postal_code = updates?.deliveryPostalCode;
    if (updates?.orderStatus !== undefined) payload.order_status = updates?.orderStatus;
    if (updates?.depositPaid !== undefined) payload.deposit_paid = updates?.depositPaid;
    if (updates?.depositPaidAt !== undefined) payload.deposit_paid_at = updates?.depositPaidAt;
    if (updates?.balancePaid !== undefined) payload.balance_paid = updates?.balancePaid;
    if (updates?.balancePaidAt !== undefined) payload.balance_paid_at = updates?.balancePaidAt;
    if (updates?.collectionPlannedDate !== undefined) payload.collection_planned_date = updates?.collectionPlannedDate;
    if (updates?.collectionDoneAt !== undefined) payload.collection_done_at = updates?.collectionDoneAt;
    if (updates?.depositInvoiceRef !== undefined) payload.deposit_invoice_ref = updates?.depositInvoiceRef;
    if (updates?.finalInvoiceRef !== undefined) payload.final_invoice_ref = updates?.finalInvoiceRef;
    if (updates?.linkedShipmentId !== undefined) payload.linked_shipment_id = updates?.linkedShipmentId;
    if (updates?.convertedAt !== undefined) payload.converted_at = updates?.convertedAt;
    if (updates?.notes !== undefined) payload.notes = updates?.notes;
    if (updates?.lastRelanceAt !== undefined) payload.last_relance_at = updates?.lastRelanceAt;
    if (updates?.relanceCount !== undefined) payload.relance_count = updates?.relanceCount;

    const { data, error } = await supabase?.from('promo_summer_orders')?.update(payload)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Update status only
  async updateStatus(id, newStatus) {
    const updates = { order_status: newStatus };
    if (newStatus === 'acompte_paye') {
      updates.deposit_paid = true;
      updates.deposit_paid_at = new Date()?.toISOString();
    }
    if (newStatus === 'solde_paye') {
      updates.balance_paid = true;
      updates.balance_paid_at = new Date()?.toISOString();
    }
    if (newStatus === 'barriques_collectees') {
      updates.collection_done_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase?.from('promo_summer_orders')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;

    // Sync client status based on new order status
    if (data?.client_id) {
      try {
        const clientStatusMap = {
          commande_creee: 'prospect_inscrit',
          barriques_a_livrer: 'prospect_inscrit',
          barriques_livrees: 'client_materiel_livre',
          acompte_paye: 'client_materiel_livre',
          en_attente_remplissage: 'client_en_attente_collecte',
          relance_necessaire: 'client_en_attente_collecte',
          collecte_planifiee: 'client_en_attente_collecte',
          barriques_collectees: 'client_en_attente_collecte',
          solde_paye: 'client_en_attente_collecte',
          converti_en_envoi: 'client_avec_envoi',
          affecte_conteneur: 'client_avec_envoi',
        };
        const newClientStatus = clientStatusMap?.[newStatus];
        if (newClientStatus) {
          const { data: clientData } = await supabase?.from('clients')?.select('client_status')?.eq('id', data?.client_id)?.single();
          const currentStatus = clientData?.client_status;
          // Only upgrade, never downgrade (except to client_avec_envoi which always wins)
          const statusRank = {
            prospect: 0, prospect_inscrit: 1, promo_customer: 1,
            client_materiel_livre: 2, client_en_attente_collecte: 3,
            client_avec_envoi: 4, client: 4, loyal_client: 5,
          };
          const currentRank = statusRank?.[currentStatus] ?? 0;
          const newRank = statusRank?.[newClientStatus] ?? 0;
          if (newRank >= currentRank) {
            await supabase?.from('clients')?.update({ client_status: newClientStatus })?.eq('id', data?.client_id);
          }
        }
      } catch (syncErr) {
        console.warn('Avertissement: sync statut client échoué:', syncErr?.message);
      }
    }

    return data;
  },

  // Mark relance
  async markRelance(id) {
    const { data: current } = await supabase?.from('promo_summer_orders')?.select('relance_count')?.eq('id', id)?.single();

    const { data, error } = await supabase?.from('promo_summer_orders')?.update({
        order_status: 'relance_necessaire',
        last_relance_at: new Date()?.toISOString(),
        relance_count: (current?.relance_count || 0) + 1,
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Get financial summary
  async getFinancialSummary() {
    const { data, error } = await supabase?.rpc('get_promo_summer_financial_summary');
    if (error) throw error;
    return data?.[0] || {};
  },

  // Get orders needing relance (en_attente_remplissage > X days)
  async getOrdersNeedingRelance(daysThreshold = 30) {
    const thresholdDate = new Date();
    thresholdDate?.setDate(thresholdDate?.getDate() - daysThreshold);

    const { data, error } = await supabase?.from('promo_summer_orders')?.select('*')?.eq('order_status', 'en_attente_remplissage')?.lt('updated_at', thresholdDate?.toISOString())?.order('updated_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // Generate deposit invoice reference
  generateDepositInvoiceRef(orderId) {
    const date = new Date();
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const shortId = orderId?.slice(-6)?.toUpperCase();
    return `ADT-ACOMPTE-${year}${month}-${shortId}`;
  },

  // Generate final invoice reference
  generateFinalInvoiceRef(orderId) {
    const date = new Date();
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const shortId = orderId?.slice(-6)?.toUpperCase();
    return `ADT-SOLDE-${year}${month}-${shortId}`;
  },

  // Convert promo order to shipment
  async convertToShipment(orderId) {
    const order = await this.getById(orderId);
    if (!order) throw new Error('Commande introuvable');

    // ─── Anti-doublon : si la commande a déjà un shipment lié, le retourner ──
    if (order?.linked_shipment_id) {
      const { data: existingShipment } = await supabase
        ?.from('shipments')
        ?.select('*')
        ?.eq('id', order?.linked_shipment_id)
        ?.single();
      if (existingShipment) {
        console.warn('Commande déjà convertie en envoi:', order?.linked_shipment_id);
        return { shipment: existingShipment, order };
      }
    }

    // ─── Mapping shipment_type depuis le type de commande promo ──────────────
    // Couvre tous les types autorisés par la contrainte shipment_type_valid_values
    const SHIPMENT_TYPE_MAP = {
      barrique: 'barrique',
      barriques: 'barrique',
      carton: 'carton',
      cartons: 'carton',
      colis: 'colis',
      meuble: 'meuble',
      meubles: 'meuble',
      palette: 'palette',
      palettes: 'palette',
      electromenager: 'electromenager',
      'électroménager': 'electromenager',
      tv: 'tv',
      bagage: 'bagage',
      bagages: 'bagage',
      matelas: 'matelas',
      document: 'document',
      documents: 'document',
      mix: 'mix',
      mixte: 'mix',
      divers: 'divers',
    };

    // Déduire le type depuis selected_product_name ou notes, fallback 'barrique'
    const rawProductName = order?.selected_product_name?.toLowerCase()?.trim() || '';
    let resolvedShipmentType = null;
    for (const [key, value] of Object.entries(SHIPMENT_TYPE_MAP)) {
      if (rawProductName?.includes(key)) {
        resolvedShipmentType = value;
        break;
      }
    }
    if (!resolvedShipmentType) {
      // Fallback : commandes promo été sont des barriques par défaut
      resolvedShipmentType = 'barrique';
    }

    // ─── Montants financiers réels depuis la commande ─────────────────────────
    const qty = order?.barrel_quantity || 1;
    const unitPrice = Number(order?.unit_promo_price) || 160.00;
    const totalAmount = qty * unitPrice;

    // Calculer le montant déjà payé (acompte + solde si payés)
    const depositPerUnit = Number(order?.unit_deposit_amount) || 50.00;
    const depositTotal = order?.deposit_paid ? qty * depositPerUnit : 0;
    const balancePerUnit = Number(order?.unit_balance_amount) || 110.00;
    const balanceTotal = order?.balance_paid ? qty * balancePerUnit : 0;
    const paidAmount = depositTotal + balanceTotal;
    const remainingAmount = Math.max(0, totalAmount - paidAmount);

    // ─── Référence shipment ───────────────────────────────────────────────────
    const shipmentRef = `ADT-PROMO-${Date.now()?.toString()?.slice(-6)}`;

    // ─── INSERT shipment avec données correctes ───────────────────────────────
    const { data: shipment, error: shipmentError } = await supabase?.from('shipments')?.insert({
        reference: shipmentRef,
        sender_name: order?.client_name || null,
        sender_phone: order?.client_phone || null,
        sender_address: order?.delivery_address || null,
        sender_city: order?.delivery_city || null,
        // Champs destinataire laissés à null — à compléter ultérieurement dans le workflow
        receiver_name: null,
        receiver_phone: null,
        receiver_address: null,
        receiver_city: null,
        receiver_country: null,
        shipment_type: resolvedShipmentType,
        current_status: 'en_attente',
        total_amount: totalAmount,
        paid_amount: paidAmount,
        remaining_amount: remainingAmount,
        notes: `Converti depuis commande promo été — ${qty} barrique(s) 270L. Total: ${totalAmount}€ | Payé: ${paidAmount}€ | Reste: ${remainingAmount}€`,
        // created_at omis : géré par DEFAULT now() côté Supabase
      })?.select()?.single();

    if (shipmentError) throw shipmentError;

    // ─── Rattacher les factures existantes au nouveau shipment ────────────────
    try {
      const { data: existingInvoices } = await supabase
        ?.from('adt_invoices')
        ?.select('id')
        ?.eq('promo_order_id', orderId);

      if (existingInvoices?.length > 0) {
        await supabase
          ?.from('adt_invoices')
          ?.update({ shipment_id: shipment?.id })
          ?.eq('promo_order_id', orderId);
      }
    } catch (invoiceErr) {
      console.warn('Avertissement: rattachement facture à envoi échoué:', invoiceErr?.message);
    }

    // ─── Mettre à jour la commande promo (atomique avec le shipment) ──────────
    const { data: updatedOrder, error: updateError } = await supabase?.from('promo_summer_orders')?.update({
        order_status: 'converti_en_envoi',
        linked_shipment_id: shipment?.id,
        converted_at: new Date()?.toISOString(),
      })?.eq('id', orderId)?.select()?.single();

    if (updateError) throw updateError;

    return { shipment, order: updatedOrder };
  },

  // Delete order
  async delete(id) {
    const { error } = await supabase?.from('promo_summer_orders')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

export default promoSummerOrderService;
